"use client";

import { talentNetworkText } from "@/i18n/talentNetwork.constants";

import { useEffect, useRef, useState } from "react";
import { useRouter } from "next/navigation";
import { useQueryClient } from "@tanstack/react-query";
import type { TalentNetworkSessionDto } from "@artha/api-contracts";
import { ResumePersonalizationSheet } from "@/modules/candidate-personalization";
import { PROFILE_HEADER_EMOJI_PATTERN } from "@/modules/candidate-personalization/personalization.constants";
import { TalentNetworkSummary } from "./TalentNetworkSummary";
import { TalentNetworkJoinSuccessModal } from "./TalentNetworkJoinSuccessModal";
import { PersonalizationApiError } from "@/modules/candidate-personalization/services/personalizationClient";
import {
  readTalentNetworkDraft,
  saveTalentNetworkDraft,
} from "../services/talentNetworkDraft";
import { TALENT_NETWORK_JOIN_ERROR, TALENT_NETWORK_PROFILE_ROUTE, TALENT_NETWORK_SUMMARY_QUERY_KEY } from "../talentNetwork.constants";
import { fetchTalentNetworkSession, joinTalentNetwork } from "../services/talentNetworkClient";
import type { TalentNetworkJoinPanelProps } from "../talentNetwork.types";
import { useAnalytics } from "@/lib/analytics/useAnalytics";
import { ANALYTICS_EVENTS } from "@/lib/analytics/events";
import { useAudienceAnalyticsContext } from "@/lib/analytics/AudienceAnalyticsContext";
import type { TalentNetworkJoinClickedEventProperties } from "@artha/types";

export function TalentNetworkJoinPanel({
  username,
  creatorName,
  controller,
  showActions = true,
}: TalentNetworkJoinPanelProps) {
  const router = useRouter();
  const { trackEvent } = useAnalytics();
  const { creatorId } = useAudienceAnalyticsContext();
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const [successOpen, setSuccessOpen] = useState(false);
  const [joining, setJoining] = useState(false);
  const [joinError, setJoinError] = useState<string | null>(null);
  const [signInRequired, setSignInRequired] = useState(false);
  const joinPending = useRef(false);
  const scope = useRef(0);
  const sessionRequest = useRef(0);
  const [session, setSession] = useState<TalentNetworkSessionDto | null>(null);
  const [sessionLoading, setSessionLoading] = useState(true);
  const displayName = creatorName?.replace(PROFILE_HEADER_EMOJI_PATTERN, "").trim() || username;
  const profileRoute = `/@${encodeURIComponent(username)}/${TALENT_NETWORK_PROFILE_ROUTE}`;
  useEffect(() => {
    let active = true;
    scope.current++;
    joinPending.current = false;
    setJoining(false);
    setSuccessOpen(false);
    setJoinError(null);
    setSignInRequired(false);
    setSession(null);
    setSessionLoading(true);
    const refresh = () => {
      if (joinPending.current) return;
      const current = ++sessionRequest.current;
      void fetchTalentNetworkSession(username)
        .then((result) => {
          if (active && current === sessionRequest.current) setSession(result);
        })
        .catch(() => undefined)
        .finally(() => {
          if (active && current === sessionRequest.current) setSessionLoading(false);
        });
    };
    refresh();
    window.addEventListener("focus", refresh);
    window.addEventListener("pageshow", refresh);
    return () => {
      active = false;
      scope.current++;
      window.removeEventListener("focus", refresh);
      window.removeEventListener("pageshow", refresh);
    };
  }, [controller.isAuthenticated, controller.externalId, username]);
  const joined =
    session?.authenticated &&
    session.membershipStatus === "active";

  const handleJoin = async () => {
    if (joinPending.current) return;
    if (joined) {
      router.push(profileRoute);
      return;
    }
    trackEvent(ANALYTICS_EVENTS.AUD_TALENT_NETWORK_JOIN_CLICKED, {
      portal_id: creatorId || controller.creatorId || "",
      username,
      source: "talent_network",
      signed_in: Boolean(session?.authenticated),
    } satisfies TalentNetworkJoinClickedEventProperties);
    if (session?.authenticated) {
      const currentScope = scope.current;
      joinPending.current = true;
      sessionRequest.current++;
      setJoining(true);
      setJoinError(null);
      try {
        const result = await joinTalentNetwork(username);
        if (currentScope !== scope.current) return;
        setSession(result);
        setSuccessOpen(true);
        void queryClient.invalidateQueries({ queryKey: TALENT_NETWORK_SUMMARY_QUERY_KEY(username) });
      } catch (error) {
        if (currentScope !== scope.current) return;
        if (error instanceof PersonalizationApiError && error.code === "CANDIDATE_SESSION_REQUIRED") {
          setSession({ authenticated: false });
          setSignInRequired(true);
          setOpen(true);
        } else {
          setJoinError(TALENT_NETWORK_JOIN_ERROR);
        }
      } finally {
        if (currentScope === scope.current) {
          joinPending.current = false;
          setJoining(false);
        }
      }
      return;
    }
    if (signInRequired) {
      setOpen(true);
    } else if (
      (controller.isAuthenticated && controller.editableProfile) ||
      readTalentNetworkDraft(username)
    ) {
      router.push(profileRoute);
    } else setOpen(true);
  };

  return (
    <>
      <TalentNetworkSummary
        username={username}
        creatorName={creatorName}
        showActions={showActions}
        joinLabel={joining ? talentNetworkText("joining") : joined ? talentNetworkText("manage_profile") : undefined}
        joinDisabled={controller.isSessionLoading || sessionLoading || joining}
        onJoin={() => void handleJoin()}
      />
      {joinError && <p role="alert" className="mt-3 text-sm text-red-600">{joinError}</p>}
      <TalentNetworkJoinSuccessModal
        creatorName={displayName}
        isOpen={successOpen}
        onClose={() => {
          setSuccessOpen(false);
          router.push(`/@${encodeURIComponent(username)}`);
        }}
      />
      <ResumePersonalizationSheet
        isOpen={open}
        onClose={() => setOpen(false)}
        controller={controller}
        initialStep={signInRequired ? "sign_in" : "upload"}
        mode="talent_network"
        creatorName={displayName}
        creatorUsername={username}
        onParsed={(result) => {
          if (!result.editableProfile) return;
          saveTalentNetworkDraft(username, {
            externalId: result.externalId,
            ownerExternalId: result.ownerExternalId,
            editableProfile: result.editableProfile,
            openToOpportunities: true,
          });
          setOpen(false);
          router.push(profileRoute);
        }}
        onComplete={() => {
          setOpen(false);
          router.push(profileRoute);
        }}
      />
    </>
  );
}
